/**
 * ContactStateContext manages unified contact state using an indexed store.
 * Replaces the former AddressBookContext with a single source of truth
 * for both address book contacts and recipient selection.
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  contactReducer,
  createEmptyIndex,
  type ContactAction,
  type ContactIndex,
  type DuplicateContactGroup,
} from './reducer';
import {
  contactsToJSON,
  contactsFromJSON,
  lookupAllContacts,
  duplicatesToJSON,
  duplicatesFromJSON,
} from './util';
import { StorageService, isQuotaError } from '@/services/storage';
import { useAppToast } from '@/hooks/useAppToast';
import { useAppStateChange } from '@/hooks/useAppState';

interface ContactStateContextType {
  state: ContactIndex;
  dispatch: React.Dispatch<ContactAction>;
  duplicatesToResolve: DuplicateContactGroup[];
  saveChanges: () => Promise<void>;
  isLoading: boolean;
}

const ContactStateContext = createContext<ContactStateContextType | undefined>(
  undefined
);

interface ContactStateProviderProps {
  children: React.ReactNode;
}

const QUOTA_TOAST_MESSAGE =
  'Your device storage is full and contacts could not be saved.';

export const ContactStateProvider: React.FC<ContactStateProviderProps> = ({
  children,
}) => {
  const [presentToast] = useAppToast();
  const [state, dispatch] = useReducer(
    contactReducer,
    undefined,
    createEmptyIndex
  );
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedInitialContacts = useRef(false);
  const previousAllIdsRef = useRef<string[]>([]);
  const previousByIdRef = useRef<Record<string, unknown>>({});
  const previousDuplicatesRef = useRef<unknown>(state.duplicatesToResolve);

  // Load saved contacts from storage on mount
  useEffect(() => {
    if (hasLoadedInitialContacts.current) {
      setIsLoading(false);
      return;
    }

    const loadContacts = async () => {
      setIsLoading(true);
      try {
        const stored = await StorageService.getAddressBook();
        if (stored) {
          try {
            const loadedContacts = contactsFromJSON(stored);
            dispatch({ type: 'SET_CONTACTS', payload: loadedContacts });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to parse stored Address Book:', error);
          }
        }
        // Duplicates an import left for the Merge duplicates prompt, so a
        // reload while it is up brings it back instead of dropping them.
        const pending = await StorageService.getPendingDuplicates();
        const duplicates = pending ? duplicatesFromJSON(pending) : [];
        if (duplicates.length > 0) {
          dispatch({ type: 'RESTORE_DUPLICATES', payload: duplicates });
        }
        hasLoadedInitialContacts.current = true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load Address Book:', error);
        hasLoadedInitialContacts.current = true;
      } finally {
        setIsLoading(false);
      }
    };
    loadContacts();
  }, []);

  // Serialize only allIds contacts for persistence
  const serializeForStorage = useCallback(() => {
    const contacts = lookupAllContacts(state);
    return JSON.stringify(contactsToJSON(contacts));
  }, [state]);

  // Write the book and the duplicates still awaiting the prompt together.
  const persist = useCallback(async () => {
    await StorageService.setAddressBook(serializeForStorage());
    await StorageService.setPendingDuplicates(
      JSON.stringify(duplicatesToJSON(state.duplicatesToResolve))
    );
  }, [serializeForStorage, state.duplicatesToResolve]);

  const showQuotaToast = useCallback(() => {
    presentToast({
      message: QUOTA_TOAST_MESSAGE,
      duration: 10000,
      color: 'danger',
    });
  }, [presentToast]);

  // Save changes to storage
  const saveChanges = useCallback(async () => {
    try {
      await persist();
      previousAllIdsRef.current = state.allIds;
      previousByIdRef.current = state.byId;
      previousDuplicatesRef.current = state.duplicatesToResolve;
    } catch (error) {
      if (isQuotaError(error)) showQuotaToast();
      // eslint-disable-next-line no-console
      console.error('Failed to save Address Book:', error);
      throw error;
    }
  }, [
    persist,
    showQuotaToast,
    state.allIds,
    state.byId,
    state.duplicatesToResolve,
  ]);

  // Debounced auto-save on allIds/byId changes
  useEffect(() => {
    if (isLoading) return;

    const hasChanged =
      state.allIds !== previousAllIdsRef.current ||
      state.byId !== previousByIdRef.current ||
      state.duplicatesToResolve !== previousDuplicatesRef.current;

    if (!hasChanged) return;

    const performSave = async () => {
      try {
        await persist();
        previousAllIdsRef.current = state.allIds;
        previousByIdRef.current = state.byId;
        previousDuplicatesRef.current = state.duplicatesToResolve;
      } catch (error) {
        if (isQuotaError(error)) showQuotaToast();
        // eslint-disable-next-line no-console
        console.error('Failed to auto-save Address Book:', error);
      }
    };

    const timeoutId = setTimeout(performSave, 1000);
    return () => clearTimeout(timeoutId);
  }, [
    state.allIds,
    state.byId,
    state.duplicatesToResolve,
    isLoading,
    persist,
    showQuotaToast,
  ]);

  // Force save when the app is backgrounded (PWA + native persistence)
  useAppStateChange(isActive => {
    if (!isActive) {
      persist().catch(error => {
        if (isQuotaError(error)) showQuotaToast();
        // eslint-disable-next-line no-console
        console.error('Failed to background-save Address Book:', error);
      });
    }
  });

  const value: ContactStateContextType = useMemo(
    () => ({
      state,
      dispatch,
      duplicatesToResolve: state.duplicatesToResolve,
      saveChanges,
      isLoading,
    }),
    [state, dispatch, saveChanges, isLoading]
  );

  return (
    <ContactStateContext.Provider value={value}>
      {children}
    </ContactStateContext.Provider>
  );
};

export const useContactState = (): ContactStateContextType => {
  const context = useContext(ContactStateContext);
  if (context === undefined) {
    throw new Error(
      'useContactState must be used within a ContactStateProvider'
    );
  }
  return context;
};
